import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import { useCallback, useRef } from 'react';
import Source from '../../models/source';
import Track from '../../models/track';

/**
 * How many tracks may fail to start, one after another, before playback stops
 * instead of advancing.
 *
 * Three rather than two because a playlist can genuinely hold a couple of
 * dead tracks in a row, and rather than five because by then the user has
 * watched the player race through half a screen of their music.
 */
export const maxConsecutiveFailures = 3;

export interface FailureOptions {
  /** The library record for the track that failed, when there is one. */
  track?: Track;
  /**
   * Whether this failure, on its own, is evidence that the *track* is bad.
   *
   * False for anything that indicts the network, the provider, the token or
   * the player's own configuration. Those failures say nothing about the file
   * and must never be written into the library.
   */
  trackFault?: boolean;
  /** What went wrong, for the message and the error report. */
  reason?: string;
}

interface Deps {
  /** Writes, or clears, the missing flag on a library track. */
  setTrackMissing(track: Track, missing?: boolean): void;
  /** Advance to the next track. */
  advance(): void;
  report(message: any, unexpectedError?: boolean): void;
}

function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export interface FailureInput {
  /** Failures so far in this run, this one included. */
  run: number;
  /** Whether a flag was written earlier in this run and still stands. */
  hasFlagged: boolean;
  /** Whether this failure implicates the track rather than everything else. */
  trackFault: boolean;
  offline: boolean;
  /** Whether there is a real library record to flag at all. */
  canFlagTrack: boolean;
}

export interface FailureDecision {
  /** Write the missing flag on this track. */
  flag: boolean;
  /** Take back the flag written earlier in this run. */
  unflag: boolean;
  /** Skip onwards. */
  advance: boolean;
  /** Tell the user playback has stopped. */
  stop: boolean;
  /** Send one report covering the whole run. */
  reportRun: boolean;
}

/**
 * The whole policy, as a pure function, because getting it wrong writes to
 * the user's library and the mistake outlives the session.
 */
export function decideFailure({
  run,
  hasFlagged,
  trackFault,
  offline,
  canFlagTrack,
}: FailureInput): FailureDecision {
  return {
    // Only the first failure of a run may accuse the track, and only when the
    // failure is the kind that says anything about it, and never when the
    // network is known to be down.
    flag: run === 1 && trackFault && !offline && canFlagTrack,
    // A second failure behind the first proves the first was not the track.
    unflag: run > 1 && hasFlagged,
    advance: run < maxConsecutiveFailures,
    stop: run >= maxConsecutiveFailures,
    // At the moment it trips, not on every attempt afterwards.
    reportRun: run === maxConsecutiveFailures,
  };
}

/**
 * Decides what a failed track means, and when to stop trying.
 *
 * Two problems live here, and they are the same problem seen from either end.
 *
 * Every error path in the player used to skip to the next track on its own.
 * Nothing counted, so a cause that affects every track - no network, a
 * provider having a bad morning, an embed misconfigured - was met by racing
 * through the entire playlist, one snackbar and one error report per track,
 * arriving nowhere. YouTube error 153 did exactly this: five tracks in
 * twenty-one seconds.
 *
 * Worse, most of those paths also flagged the track `missing` first. That
 * flag is persisted to the user's own cloud library, so a bad few minutes on
 * a train could mark a few hundred perfectly good tracks as broken, and
 * clearing it means playing every one of them again. The Drive branch already
 * had a careful classifier guarding against precisely this; nothing else did.
 *
 * So: a failure only implicates the track when it is *isolated*. The first
 * one is flagged optimistically, and the flag is taken back the moment a
 * second failure follows it, because a run is evidence of a common cause
 * rather than of many broken files. Nothing is ever flagged while the browser
 * says it is offline.
 */
export default function usePlaybackFailures({ setTrackMissing, advance, report }: Deps) {
  // Refs throughout: these are read from event listeners and promise
  // callbacks that outlive the render they were created in, and the player
  // rebuilds all three on most renders.
  const setTrackMissingRef = useRef(setTrackMissing);
  setTrackMissingRef.current = setTrackMissing;
  const advanceRef = useRef(advance);
  advanceRef.current = advance;
  const reportRef = useRef(report);
  reportRef.current = report;

  /** Failures since the last track that actually started playing. */
  const consecutive = useRef(0);
  /** The track flagged on the first failure of the current run, if any. */
  const flagged = useRef<Track>(undefined);
  /** Reasons in this run, oldest first, for one report rather than many. */
  const reasons = useRef<string[]>([]);

  /**
   * A track really started. Whatever came before it was not systemic, so the
   * run is over and any flag written during it stands.
   */
  const notePlaybackStarted = useCallback(function notePlaybackStarted() {
    consecutive.current = 0;
    flagged.current = undefined;
    reasons.current = [];
  }, []);

  const failPlayback = useCallback(function failPlayback({
    track,
    trackFault = false,
    reason,
  }: FailureOptions = {}) {
    consecutive.current += 1;
    const run = consecutive.current;
    const offline = isOffline();

    if (reason) {
      reasons.current.push(reason);
    }

    const decision = decideFailure({
      run,
      hasFlagged: !!flagged.current,
      trackFault,
      offline,
      canFlagTrack: !!track && track.type !== Source.Null,
    });

    if (decision.flag && track) {
      flagged.current = track;
      setTrackMissingRef.current(track, true);
    }

    if (decision.unflag && flagged.current) {
      // Taken back before it can reach the user's cloud library.
      setTrackMissingRef.current(flagged.current, false);
      flagged.current = undefined;
    }

    if (decision.advance) {
      advanceRef.current();
      return;
    }

    // Stop. There is no separate playing flag to clear: playback is driven by
    // the current track changing, so declining to advance *is* the pause.
    reportRef.current(
      offline
        ? 'Playback stopped: this device appears to be offline. ' +
            'Nothing has been marked as missing.'
        : `Playback stopped after ${run} tracks in a row would not start. ` +
            'This usually means a connection or sign-in problem rather than the tracks. ' +
            'Nothing has been marked as missing.',
      false,
    );

    // One report for the run, at the moment it trips, rather than one per
    // track. Sending these as errors per track is what made a single
    // misconfigured embed look like five separate incidents.
    if (decision.reportRun) {
      Sentry.withScope((scope) => {
        scope.setExtra('consecutiveFailures', run);
        scope.setExtra('offline', offline);
        scope.setExtra('reasons', reasons.current);
        scope.setExtra('lastTrackType', track ? Source[track.type] : 'unknown');

        Sentry.captureMessage(
          `Playback stopped after ${run} consecutive failures`,
          Severity.Warning,
        );
      });
    }
  }, []);

  return {
    failPlayback,
    notePlaybackStarted,
  };
}
